import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BlogCarousel from '../components/common/BlogCarousel';
import { Link } from 'react-router-dom';
import TickerSearchInput from '../components/common/TickerSearchInput';
import { useAuth } from '../hooks/useAuth';
import Footer from '../components/common/Footer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [stockSymbol, setStockSymbol] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect to dashboard if user is already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stockSymbol.trim()) return;
    
    setIsLoading(true);
    try {
      navigate(`/check-eligibility?ticker=${stockSymbol.toUpperCase()}`);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        {/* Top Utility Bar */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end items-center h-10 text-sm">
              {/* <a href="#demo" className="text-gray-700 hover:text-navy-700 ml-6">Schedule a Demo</a> */}
              <button
                onClick={() => navigate('/customer-service')}
                className="text-gray-700 hover:text-navy-700 ml-6"
              >
                Customer Service
              </button>
              <button 
                onClick={() => navigate('/broker/register')}
                className="ml-6 text-primary-600 font-medium hover:text-primary-700"
              >
                Partner Signup
              </button>
              <Link to="/blog" className="ml-6 text-gray-700 hover:text-navy-700">Blog</Link>
              <Link to="/glossary" className="ml-6 text-gray-700 hover:text-navy-700">Glossary</Link>
              <Link to="/faq" className="ml-6 text-gray-700 hover:text-navy-700">FAQ</Link>
              <Link to="/customer-service" className="ml-6 text-gray-700 hover:text-navy-700">Help</Link>
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Dashboard
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            {/* Logo - Increased size */}
            <div className="flex items-center h-full py-4">
              <img 
                src="/24lp-logo-primary.png"
                alt="Twenty Four Capital" 
                className="h-20 md:h-24 w-auto object-contain block"
              />
            </div>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-gray-700 hover:text-navy-700 font-medium">How it Works</a>
              <a href="#platform-features" className="text-gray-700 hover:text-navy-700 font-medium">Platform Features</a>
              <a href="#why-us" className="text-gray-700 hover:text-navy-700 font-medium">Why Choose Us</a>
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => navigate('/check-eligibility')}
              className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
            >
              Get Started
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="hero" className="bg-gradient-to-br from-navy-800 to-navy-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Twenty Four Capital
            </h1>
            <p className="text-xl mb-10 text-gray-100">
              Setting a new standard for how publicly traded issuers access predictable and low cost growth capital.
            </p>

            {/* Key Benefits */}
            <div className="flex justify-center items-center space-x-8 mb-12 flex-wrap gap-6">
              <div className="flex items-center">
                <svg className="h-6 w-6 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Instant Eligibility</span>
              </div>
              <div className="flex items-center">
                <svg className="h-6 w-6 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Automated Process</span>
              </div>
              <div className="flex items-center">
                <svg className="h-6 w-6 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Mobile Access</span>
              </div>
            </div>

            {/* Search Form */}
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <TickerSearchInput
                    value={stockSymbol}
                    onChange={(value) => setStockSymbol(value)}
                    onSelect={(option) => {
                      setStockSymbol(option.symbol);
                      // Auto-submit when selecting from dropdown
                      setTimeout(() => {
                        const form = document.querySelector('form');
                        form?.requestSubmit();
                      }, 100);
                    }}
                    placeholder="Enter company name or stock symbol (e.g., AAPL, Apple)"
                    disabled={isLoading}
                    showPopularOnFocus={true}
                    className="w-full"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !stockSymbol.trim()}
                  className="bg-orange-500 text-white px-8 py-3 text-lg font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Checking...' : 'Check Eligibility'}
                </button>
              </form>
              
              {/* App Store Badges */}
              {/* <div className="flex justify-center items-center space-x-4 mt-8">
                <a href="#app-store" className="hover:opacity-90 transition-opacity">
                  <img src="/app-store-badge.svg" alt="Download on the App Store" className="h-12 w-auto" />
                </a>
                <a href="#google-play" className="hover:opacity-90 transition-opacity">
                  <img src="/google-play-badge.svg" alt="Get it on Google Play" className="h-12 w-auto" />
                </a>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How it Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From eligibility check to facility management - a complete digital experience
            </p>
          </div>

          {/* Getting Started Process */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">Getting Started</h3>
            
            {/* Timeline */}
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>
              
              {/* Step 1 */}
              <div className="relative flex items-center mb-12 md:mb-16">
                <div className="flex-1 md:text-right md:pr-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Instant Eligibility Check</h4>
                  <p className="text-gray-600">Enter your stock symbol and get immediate feedback on your company's eligibility based on real-time market data.</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold z-10">1</div>
                <div className="flex-1 md:pl-8"></div>
              </div>

              {/* Step 2 */}
              <div className="relative flex items-center mb-12 md:mb-16">
                <div className="flex-1 md:pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold z-10">2</div>
                <div className="flex-1 md:text-left md:pl-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Select Your Terms</h4>
                  <p className="text-gray-600">Review customized financing options tailored to your company's needs and choose your preferred terms.</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex items-center mb-12 md:mb-16">
                <div className="flex-1 md:text-right md:pr-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Advance Draw Notice</h4>
                  <p className="text-gray-600">Elect for an Advance Draw Notice with funding available as fast as the same business day.</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold z-10">3</div>
                <div className="flex-1 md:pl-8"></div>
              </div>

              {/* Step 4 */}
              <div className="relative flex items-center mb-12 md:mb-16">
                <div className="flex-1 md:pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold z-10">4</div>
                <div className="flex-1 md:text-left md:pl-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Create Account & Verify</h4>
                  <p className="text-gray-600">Set up your secure account and confirm your company information through our streamlined process.</p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="relative flex items-center mb-12 md:mb-16">
                <div className="flex-1 md:text-right md:pr-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Review & Sign Documents</h4>
                  <p className="text-gray-600">Instantly receive final deal documents to review, share with stakeholders, or electronically sign.</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold z-10">5</div>
                <div className="flex-1 md:pl-8"></div>
              </div>

              {/* Step 6 */}
              <div className="relative flex items-center">
                <div className="flex-1 md:pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-navy-700 rounded-full flex items-center justify-center text-white font-bold z-10">✓</div>
                <div className="flex-1 md:text-left md:pl-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Ready to Use</h4>
                  <p className="text-gray-600">Your facility is active! Access your dashboard to monitor usage, submit notices, and manage your equity facility.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Equity Finance Made Simple - Moved to end of How it Works */}
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Equity Finance Made Simple</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of equity financing with our streamlined digital platform
            </p>
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section id="platform-features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Platform Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced technology designed to simplify complex equity instruments
            </p>
          </div>

          {/* Feature 1: Daily Eligibility Updates */}
          <div className="mb-20">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <div className="flex items-start mb-6">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Daily Eligibility Updates and Real-Time Tracking</h3>
                  <p className="text-gray-600 mb-6">
                    Simply login to your dashboard using our web portal or mobile application and view an overview of your active facility including:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Maximum Exercise Notices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Total Shares Available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">At-the-Market & Exchange Cap Compliance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Real-Time Transaction Tracker</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Miscellaneous Facility Overview</span>
                    </li>
                  </ul>
                  <p className="text-gray-600 mt-6">
                    All of the features have been designed to simplify monitoring and managing complex equity instruments. Take the guesswork out of tracking payments, settlement dates, share issuances, and all of the tedious tasks that typically accompany utilizing equity-based financing structures.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Automated Document Generation */}
          <div className="mb-20">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <div className="flex items-start mb-6">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Automated Document Generation</h3>
                  <p className="text-gray-600 mb-6">
                    From our underwriting and accessing term sheets to the final deal documents and exercise notices, we've automated the process of generating all of the transaction documents in a matter of seconds using your specific transaction terms.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Access a term sheet in a matter of seconds</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Create your account and confirm your company information to immediately access your final deal documents</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">Exercise purchase notices in a couple of clicks from your Company dashboard</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">All transaction documents are organized, stored, and easily accessible</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">From term sheet to closing check list in record speed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3: Unmatched Precision, Speed, and Flexibility */}
          <div className="mb-20">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <div className="flex items-start mb-6">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Unmatched Precision, Speed, and Flexibility</h3>
                  <p className="text-gray-600 mb-6">
                    Through the automated workflows opens a door for transaction mechanisms that have historically been far too encumbersome for investors to manage.
                  </p>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Volume Thresholds</h4>
                      <p className="text-gray-600">
                        Following an exercise notice, our system automatically logs the volume and subsequently tracks the trading volume. The Valuation Period is structured to conclude early when 10% of the trading volume is equal to or greater than the number of shares subject to the purchase notice.
                      </p>
                      <p className="text-gray-600 mt-2 italic">
                        This allows Companies confidence in knowing that their facility has the ability to exercise several purchase notices in a single trading session if market conditions are favorable.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Automatic Floor Price Adjustment</h4>
                      <p className="text-gray-600">
                        Monitoring of the historical share issuances and compliance with the Exchange Cap (19.99% of Outstanding Shares) with a real-time adjustment to the pricing mechanism upon reaching the 19.99% threshold. Following, the purchase price is adjusted to reflect the current At-the-Market Pricing, providing a highly competitive instrument for accessing low cost growth capital.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Three Distinct Exercise Notices</h4>
                      <p className="text-gray-600">
                        Flexibility redefined with various exercise notices that each accomplish a specific use case and are each monitored for daily eligibility and accessible with only a couple clicks from the company dashboard.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Transaction Tracker</h4>
                      <p className="text-gray-600">
                        Each exercise notice is followed with a real-time tracker that provides estimated settlement dates, current progress, and provides certainty in capital management.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Platform</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're transforming equity financing with technology, transparency, and speed
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">14</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Days to Close</h3>
              <p className="text-gray-300">From application to funding in just 14 business days</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">24/7</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Platform Access</h3>
              <p className="text-gray-300">Manage your facility anytime, anywhere</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">0</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Hidden Fees</h3>
              <p className="text-gray-300">Transparent pricing with no surprises</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">100%</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Digital Process</h3>
              <p className="text-gray-300">Complete everything online from start to finish</p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Carousel */}
      <BlogCarousel />

      <Footer />
    </div>
  );
};

export default LandingPage; 